/**
 * XSS Sanitization Utilities (Task #4211)
 *
 * Provides centralized sanitization for user-controlled data to prevent XSS attacks.
 * Uses DOMPurify to strip malicious content while preserving legitimate text.
 *
 * SECURITY: All user-controlled data MUST be sanitized before rendering.
 * This includes: names, emails, file names, case titles, error messages.
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize text content by stripping ALL HTML tags.
 * Use for: names, emails, file names, case numbers, identifiers.
 *
 * @param input - User-controlled string to sanitize
 * @returns Sanitized string with all HTML stripped, or empty string if null/undefined
 *
 * @example
 * sanitizeText('<script>alert("XSS")</script>John') // Returns: 'John'
 * sanitizeText("O'Connor") // Returns: "O'Connor" (preserves special chars)
 * sanitizeText('<img src=x onerror=alert(1)>') // Returns: ''
 */
export const sanitizeText = (input: string | undefined | null): string => {
  if (input === null || input === undefined) {
    return '';
  }
  // ALLOWED_TAGS: [] strips ALL HTML tags, keeping only text content
  // This is the safest option for user names, emails, file names, etc.
  return DOMPurify.sanitize(String(input), { ALLOWED_TAGS: [] });
};

/**
 * Sanitize error messages from API responses.
 * Same as sanitizeText but provides semantic clarity for error handling.
 *
 * @param error - Error message string to sanitize
 * @returns Sanitized error message
 *
 * @example
 * sanitizeError('User <script>alert(1)</script> not found') // Returns: 'User  not found'
 */
export const sanitizeError = (error: string | undefined | null): string => {
  return sanitizeText(error);
};

/**
 * Sanitize a number value, returning it as-is or a default.
 * Numbers are inherently safe but this provides consistency.
 *
 * @param input - Number to sanitize
 * @param defaultValue - Default value if input is null/undefined
 * @returns The number or default value
 */
export const sanitizeNumber = (
  input: number | undefined | null,
  defaultValue: number = 0
): number => {
  if (input === null || input === undefined || isNaN(input)) {
    return defaultValue;
  }
  return input;
};

/**
 * Sanitize and format file size for display.
 * Ensures numeric input is valid before formatting.
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export const sanitizeFileSize = (bytes: number | undefined | null): string => {
  const safeBytes = sanitizeNumber(bytes, 0);
  if (safeBytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(safeBytes) / Math.log(k));
  return parseFloat((safeBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate and sanitize a URL to prevent javascript: and other malicious protocols.
 * Only allows http: and https: protocols for safe navigation.
 *
 * SECURITY: CRITICAL - Always use this when assigning URLs to window.location.href
 * or any other navigation/redirect scenario.
 *
 * @param url - URL string to validate
 * @returns The URL if safe, or null if potentially malicious
 * @throws Error if URL is malformed or uses forbidden protocol
 *
 * @example
 * sanitizeUrl('https://example.com/file.pdf') // Returns: 'https://example.com/file.pdf'
 * sanitizeUrl('javascript:alert(1)') // Throws: Error
 * sanitizeUrl('data:text/html,...') // Throws: Error
 */
export const sanitizeUrl = (url: string | undefined | null): string => {
  if (!url) {
    throw new Error('URL is required');
  }

  try {
    // Parse URL - will throw on malformed URLs
    const parsed = new URL(url, window.location.origin);

    // WHITELIST: Only allow safe protocols
    const allowedProtocols = ['http:', 'https:'];
    if (!allowedProtocols.includes(parsed.protocol)) {
      throw new Error(`Forbidden URL protocol: ${parsed.protocol}`);
    }

    return url;
  } catch (error) {
    if (error instanceof Error && error.message.includes('Forbidden URL protocol')) {
      throw error;
    }
    throw new Error(`Invalid URL: ${url}`);
  }
};
